const STANDARD_LINK_KEYS = Object.freeze(["home", "catalog", "about", "highlights", "contact"]);

function objectValue(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function clone(value) {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
}

function hasValue(value) {
  return value !== undefined && value !== null && !(typeof value === "string" && value.trim() === "");
}

function configured(value, fallback) {
  const candidate = String(value || "").trim();
  return candidate || fallback;
}

function finiteOr(value, fallback) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function mergeMissing(target, defaults) {
  const output = objectValue(target);
  Object.entries(objectValue(defaults)).forEach(([key, value]) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      output[key] = mergeMissing(objectValue(output[key]), value);
      return;
    }
    if (!hasValue(output[key])) output[key] = clone(value);
  });
  return output;
}

function uniqueButtonIds(buttons) {
  const used = new Set();
  return (Array.isArray(buttons) ? buttons : []).slice(0, 8).map((raw, index) => {
    const item = { ...objectValue(raw) };
    const source = String(item.id || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    const seed = source || `nav-${index + 1}`;
    let id = seed;
    let suffix = 2;
    while (used.has(id)) id = `${seed}-${suffix++}`;
    used.add(id);
    item.id = id;
    return item;
  });
}

function navbarSegmentVisualStyle(settings) {
  const segments = Array.isArray(settings?.layout?.segments) ? settings.layout.segments : [];
  const segment = segments.find((candidate) => candidate?.id === "navbar") || segments.find((candidate) => candidate?.type === "navbar");
  return objectValue(segment?.visualStyle);
}

function legacyPalette(theme, visualStyle) {
  const local = visualStyle?.useGlobalColors === false ? objectValue(visualStyle?.colors) : {};
  return {
    primary: configured(local.primary, theme?.primaryColor || "#F35A3A"),
    secondary: configured(local.secondary, theme?.secondaryColor || "#19242F"),
    surface: configured(local.surface, theme?.surfaceColor || "#FFFFFF"),
    background: configured(local.background, theme?.backgroundColor || "#EEF0F5"),
    text: configured(local.text, theme?.textColor || "#1A1A2E"),
    mutedText: configured(local.mutedText, theme?.mutedTextColor || "#5B5D73"),
    divider: configured(local.divider, theme?.dividerColor || "rgba(26,26,46,.09)"),
    onPrimary: configured(local.onPrimary, "#FFFFFF"),
    onSecondary: configured(local.onSecondary, "#FFFFFF"),
  };
}

function desktopLinkDefaults(palette, dark = false, bordered = false) {
  return {
    textColor: dark ? "rgba(255,255,255,.78)" : palette.mutedText,
    backgroundColor: "transparent",
    borderColor: bordered ? (dark ? "rgba(255,255,255,.24)" : palette.divider) : "transparent",
    hoverTextColor: dark ? palette.onSecondary : palette.secondary,
    hoverBackgroundColor: dark ? "rgba(255,255,255,.1)" : "rgba(0,0,0,.04)",
    hoverBorderColor: bordered ? (dark ? "rgba(255,255,255,.4)" : palette.primary) : "transparent",
    activeTextColor: dark ? "#FFFFFF" : palette.secondary,
    activeBackgroundColor: dark ? "rgba(255,255,255,.12)" : "rgba(0,0,0,.045)",
    activeBorderColor: bordered ? (dark ? "rgba(255,255,255,.4)" : palette.primary) : "transparent",
    activeIndicatorColor: palette.primary,
    endIconColor: dark ? "rgba(255,255,255,.78)" : palette.mutedText,
  };
}

function mobileLinkDefaults(palette, mobile, dark = false, bordered = false) {
  const text = configured(mobile?.drawerTextColor, palette.text);
  const muted = configured(mobile?.drawerMutedTextColor, palette.mutedText);
  const divider = configured(mobile?.drawerDividerColor, palette.divider);
  const hoverBackground = configured(mobile?.itemHoverBackgroundColor, palette.background);
  const sharedWeight = Number.isFinite(Number(mobile?.fontWeight)) ? Number(mobile.fontWeight) : null;
  return {
    textColor: text,
    backgroundColor: "transparent",
    borderColor: bordered ? divider : "transparent",
    hoverTextColor: text,
    hoverBackgroundColor: hoverBackground,
    hoverBorderColor: bordered ? divider : "transparent",
    activeTextColor: dark ? palette.onSecondary : palette.secondary,
    activeBackgroundColor: configured(mobile?.itemHoverBackgroundColor, dark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.045)"),
    activeBorderColor: divider,
    activeIndicatorColor: palette.primary,
    endIconColor: muted,
    fontSize: finiteOr(mobile?.fontSize, 16),
    fontWeight: sharedWeight ?? 800,
    activeFontWeight: sharedWeight ?? 900,
    minHeight: finiteOr(mobile?.itemMinHeight, 52),
    borderRadius: 18,
    focusColor: palette.primary,
  };
}

function actionDefaults(palette, dark, compact = false) {
  return {
    color: dark ? palette.onSecondary : palette.secondary,
    backgroundColor: compact ? (dark ? "rgba(255,255,255,.08)" : palette.surface) : "transparent",
    borderColor: compact ? (dark ? "rgba(255,255,255,.2)" : palette.divider) : "transparent",
    hoverColor: dark ? palette.onSecondary : palette.secondary,
    hoverBackgroundColor: dark ? "rgba(255,255,255,.16)" : palette.background,
    hoverBorderColor: dark ? "rgba(255,255,255,.4)" : palette.primary,
    badgeBackgroundColor: palette.primary,
    badgeTextColor: palette.onPrimary,
    avatarBackgroundColor: dark ? palette.primary : palette.secondary,
    avatarTextColor: "#FFFFFF",
    mutedTextColor: palette.mutedText,
  };
}

function drawerLoginDefaults(palette) {
  return {
    color: palette.onPrimary,
    backgroundColor: palette.primary,
    borderColor: palette.primary,
    hoverColor: palette.onSecondary,
    hoverBackgroundColor: palette.secondary,
    hoverBorderColor: palette.secondary,
  };
}

function accountDrawerDefaults(palette, mobile) {
  const primaryTextColor = configured(mobile?.drawerTextColor, palette.text);
  const containerBorderColor = configured(mobile?.drawerDividerColor, palette.divider);
  return {
    containerBackgroundColor: palette.surface,
    containerBorderColor,
    primaryTextColor,
    secondaryTextColor: configured(mobile?.drawerMutedTextColor, palette.mutedText),
    avatarBackgroundColor: palette.secondary,
    avatarTextColor: "#FFFFFF",
    logoutColor: primaryTextColor,
    logoutBackgroundColor: palette.surface,
    logoutBorderColor: containerBorderColor,
    logoutHoverColor: primaryTextColor,
    logoutHoverBackgroundColor: palette.background,
    logoutHoverBorderColor: containerBorderColor,
    logoutFocusColor: "#F35A3A",
  };
}

function materializeAccountDrawerStyle(raw, defaults) {
  const source = objectValue(raw);
  const output = { ...source };
  const legacy = {
    containerBackgroundColor: source.backgroundColor,
    containerBorderColor: source.borderColor,
    primaryTextColor: source.color,
    secondaryTextColor: source.mutedTextColor,
    avatarBackgroundColor: source.avatarBackgroundColor,
    avatarTextColor: source.avatarTextColor,
    logoutColor: source.color,
    logoutBackgroundColor: source.backgroundColor,
    logoutBorderColor: source.borderColor,
    logoutHoverColor: source.hoverColor,
    logoutHoverBackgroundColor: source.hoverBackgroundColor,
    logoutHoverBorderColor: source.hoverBorderColor,
  };
  const materialized = mergeMissing(mergeMissing(output, legacy), defaults);
  ["color", "backgroundColor", "borderColor", "hoverColor", "hoverBackgroundColor", "hoverBorderColor", "mutedTextColor", "badgeBackgroundColor", "badgeTextColor"].forEach((key) => delete materialized[key]);
  return materialized;
}

function iconButtonDefaults(mobile) {
  const borderColor = configured(mobile?.hamburgerBorderColor, "rgba(26,26,46,.09)");
  const color = configured(mobile?.hamburgerColor, "#19242F");
  return {
    color,
    backgroundColor: configured(mobile?.hamburgerBackgroundColor, "#FFFFFF"),
    borderColor,
    hoverColor: configured(mobile?.hamburgerHoverColor, color),
    hoverBackgroundColor: configured(mobile?.hamburgerHoverBackgroundColor, "#EEF0F5"),
    hoverBorderColor: borderColor,
    focusColor: "#F35A3A",
    size: finiteOr(mobile?.hamburgerSize, 42),
  };
}

export function createNavbarVisualOwnershipDefaults(theme = {}, navigation = {}, visualStyle = {}) {
  const nav = objectValue(navigation);
  const mobile = objectValue(nav.mobile);
  const palette = legacyPalette(objectValue(theme), objectValue(visualStyle));
  const dark = nav.style === "style-3";
  const background = dark ? palette.secondary : palette.surface;
  const border = dark ? "rgba(255,255,255,.12)" : palette.divider;
  const shadow = nav.style === "style-2"
    ? "0 12px 34px rgba(20,25,35,.08)"
    : dark
      ? "0 10px 30px rgba(0,0,0,.12)"
      : "0 8px 30px rgba(20,25,35,.05)";
  const desktopLink = desktopLinkDefaults(palette, dark, false);
  const mobileLink = mobileLinkDefaults(palette, mobile, dark, false);
  const drawerBackground = configured(mobile.drawerBackgroundColor, palette.background);
  const drawerBorder = configured(mobile.drawerDividerColor, palette.divider);
  const mobileIconButton = iconButtonDefaults(mobile);
  const links = Object.fromEntries(STANDARD_LINK_KEYS.map((key) => [key, {
    desktopStyle: clone(desktopLink),
    mobileStyle: clone(mobileLink),
  }]));

  return {
    shellStyle: { backgroundColor: background, borderColor: border, boxShadow: shadow },
    barStyle: { backgroundColor: background, borderColor: border, boxShadow: "none" },
    topbarStyle: {
      backgroundColor: palette.secondary,
      borderColor: "rgba(255,255,255,.1)",
      textColor: "#FFFFFF",
      iconColor: palette.primary,
      socialIconColor: "#FFFFFF",
      socialHoverBackgroundColor: "rgba(255,255,255,.1)",
    },
    logoSurfaceStyle: {
      desktop: dark
        ? { backgroundColor: "rgba(255,255,255,.96)", borderColor: "transparent", boxShadow: "0 8px 24px rgba(0,0,0,.12)", paddingX: 8, paddingY: 4.4, borderRadius: 17.6 }
        : { backgroundColor: "transparent", borderColor: "transparent", boxShadow: "none", paddingX: 0, paddingY: 0, borderRadius: 0 },
      mobile: dark
        ? { backgroundColor: "rgba(255,255,255,.96)", borderColor: "transparent", boxShadow: "0 8px 24px rgba(0,0,0,.12)", paddingX: 6.4, paddingY: 4.4, borderRadius: 17.6 }
        : { backgroundColor: "transparent", borderColor: "transparent", boxShadow: "none", paddingX: 0, paddingY: 0, borderRadius: 0 },
      drawer: { backgroundColor: "transparent", borderColor: "transparent", boxShadow: "none", paddingX: 0, paddingY: 0, borderRadius: 0 },
    },
    businessNameStyle: {
      desktop: { color: dark ? palette.onSecondary : palette.secondary },
      mobile: { color: dark ? palette.onSecondary : palette.secondary },
      drawer: { color: configured(mobile.drawerTextColor, palette.text) },
    },
    links,
    loginStyles: {
      desktop: actionDefaults(palette, dark, false),
      mobile: actionDefaults(palette, dark, true),
      drawer: drawerLoginDefaults(palette),
    },
    accountStyles: {
      desktop: actionDefaults(palette, dark, true),
      mobile: actionDefaults(palette, dark, true),
      drawer: accountDrawerDefaults(palette, mobile),
    },
    cartStyles: {
      desktop: actionDefaults(palette, dark, true),
      mobile: actionDefaults(palette, dark, true),
    },
    mobile: {
      shellStyle: { backgroundColor: background, borderColor: border, boxShadow: shadow },
      barStyle: { backgroundColor: background, borderColor: border, boxShadow: "none" },
      drawerStyle: { backgroundColor: drawerBackground, borderColor: drawerBorder, boxShadow: "none" },
      drawerHeaderStyle: { backgroundColor: drawerBackground, borderColor: drawerBorder, boxShadow: "none" },
      openButtonStyle: clone(mobileIconButton),
      closeButtonStyle: clone(mobileIconButton),
      catalogPanelStyle: { backgroundColor: "#FFFFFF", borderColor: "rgba(26,26,46,.09)", boxShadow: "none" },
      catalogItemStyle: { textColor: "#1A1A2E", mutedTextColor: "#5B5D73", hoverBackgroundColor: "#EEF0F5", focusColor: "#F35A3A" },
      itemGap: finiteOr(mobile.itemGap, 5.2),
      logoColor: configured(mobile.logoColor, ""),
      logoMaxHeight: mobile.logoMaxHeight !== null && mobile.logoMaxHeight !== undefined && mobile.logoMaxHeight !== "" && Number.isFinite(Number(mobile.logoMaxHeight)) ? Number(mobile.logoMaxHeight) : null,
    },
    customButtonDesktopStyle: desktopLinkDefaults(palette, dark, true),
    customButtonMobileStyle: mobileLinkDefaults(palette, mobile, dark, true),
  };
}

function legacyElementStyle(raw) {
  const source = objectValue(raw);
  return {
    ...(hasValue(source.color) ? { textColor: source.color } : {}),
    ...(hasValue(source.background) ? { backgroundColor: source.background } : {}),
    ...(hasValue(source.borderColor) ? { borderColor: source.borderColor } : {}),
    ...(hasValue(source.hoverColor) ? { hoverTextColor: source.hoverColor } : {}),
    ...(hasValue(source.hoverBackground) ? { hoverBackgroundColor: source.hoverBackground } : {}),
  };
}

function linkKeyForPath(path) {
  if (path === "navigation.homeLabel") return "home";
  if (path === "navigation.productsLabel") return "catalog";
  if (path === "navigation.aboutLabel") return "about";
  if (path === "navigation.highlightsLabel") return "highlights";
  if (path === "navigation.contactLabel") return "contact";
  return "";
}

export function materializeLegacyNavbarOwnership(settings = {}) {
  const source = clone(objectValue(settings));
  const nav = objectValue(source.navigation);
  source.navigation = nav;
  const visualStyle = navbarSegmentVisualStyle(source);
  const defaults = createNavbarVisualOwnershipDefaults(source.theme, nav, visualStyle);

  nav.shellStyle = objectValue(nav.shellStyle);
  nav.barStyle = objectValue(nav.barStyle);
  nav.topbarStyle = objectValue(nav.topbarStyle);
  nav.logoSurfaceStyle = objectValue(nav.logoSurfaceStyle);
  ["desktop", "mobile", "drawer"].forEach((device) => {
    nav.logoSurfaceStyle[device] = objectValue(nav.logoSurfaceStyle[device]);
  });
  nav.businessNameStyle = objectValue(nav.businessNameStyle);
  nav.links = objectValue(nav.links);
  STANDARD_LINK_KEYS.forEach((key) => {
    nav.links[key] = objectValue(nav.links[key]);
    nav.links[key].desktopStyle = objectValue(nav.links[key].desktopStyle);
    nav.links[key].mobileStyle = objectValue(nav.links[key].mobileStyle);
  });
  nav.loginStyles = objectValue(nav.loginStyles);
  nav.accountStyles = objectValue(nav.accountStyles);
  nav.cartStyles = objectValue(nav.cartStyles);
  ["desktop", "mobile", "drawer"].forEach((device) => {
    nav.businessNameStyle[device] = objectValue(nav.businessNameStyle[device]);
    nav.loginStyles[device] = objectValue(nav.loginStyles[device]);
    nav.accountStyles[device] = objectValue(nav.accountStyles[device]);
  });
  ["desktop", "mobile"].forEach((device) => {
    nav.cartStyles[device] = objectValue(nav.cartStyles[device]);
  });
  nav.mobile = objectValue(nav.mobile);
  ["shellStyle", "barStyle", "drawerStyle", "drawerHeaderStyle", "openButtonStyle", "closeButtonStyle", "catalogPanelStyle", "catalogItemStyle"].forEach((key) => {
    nav.mobile[key] = objectValue(nav.mobile[key]);
  });

  // Legacy values are materialized only into missing owner fields. Explicit owner values always win.
  const elementColors = objectValue(visualStyle.elementColors);
  Object.entries(elementColors).forEach(([path, raw]) => {
    const linkKey = linkKeyForPath(path);
    const patch = legacyElementStyle(raw);
    if (linkKey && Object.keys(patch).length) {
      nav.links[linkKey].desktopStyle = mergeMissing(nav.links[linkKey].desktopStyle, patch);
      nav.links[linkKey].mobileStyle = mergeMissing(nav.links[linkKey].mobileStyle, patch);
    }
    if (path === "business.name" && hasValue(raw?.color)) {
      ["desktop", "mobile", "drawer"].forEach((device) => {
        nav.businessNameStyle[device] = mergeMissing(nav.businessNameStyle[device], { color: raw.color });
      });
    }
    if (path === "navigation.loginLabel" && Object.keys(patch).length) {
      const actionPatch = {
        ...(patch.textColor ? { color: patch.textColor } : {}),
        ...(patch.backgroundColor ? { backgroundColor: patch.backgroundColor } : {}),
        ...(patch.borderColor ? { borderColor: patch.borderColor } : {}),
        ...(patch.hoverTextColor ? { hoverColor: patch.hoverTextColor } : {}),
        ...(patch.hoverBackgroundColor ? { hoverBackgroundColor: patch.hoverBackgroundColor } : {}),
      };
      nav.loginStyles.desktop = mergeMissing(nav.loginStyles.desktop, actionPatch);
      nav.loginStyles.mobile = mergeMissing(nav.loginStyles.mobile, actionPatch);
      nav.loginStyles.drawer = mergeMissing(nav.loginStyles.drawer, actionPatch);
    }
  });

  nav.buttons = uniqueButtonIds(nav.buttons).map((button, index) => {
    const legacy = legacyElementStyle(elementColors[`navigation.buttons.${index}.label`]);
    return {
      ...button,
      desktopStyle: mergeMissing(mergeMissing(objectValue(button.desktopStyle), legacy), defaults.customButtonDesktopStyle),
      mobileStyle: mergeMissing(mergeMissing(objectValue(button.mobileStyle), legacy), defaults.customButtonMobileStyle),
    };
  });

  nav.shellStyle = mergeMissing(nav.shellStyle, defaults.shellStyle);
  nav.barStyle = mergeMissing(nav.barStyle, defaults.barStyle);
  nav.topbarStyle = mergeMissing(nav.topbarStyle, defaults.topbarStyle);
  nav.logoSurfaceStyle = mergeMissing(nav.logoSurfaceStyle, defaults.logoSurfaceStyle);
  nav.businessNameStyle = mergeMissing(nav.businessNameStyle, defaults.businessNameStyle);
  nav.links = mergeMissing(nav.links, defaults.links);
  nav.loginStyles = mergeMissing(nav.loginStyles, defaults.loginStyles);
  nav.accountStyles.drawer = materializeAccountDrawerStyle(nav.accountStyles.drawer, defaults.accountStyles.drawer);
  nav.accountStyles = mergeMissing(nav.accountStyles, defaults.accountStyles);
  nav.cartStyles = mergeMissing(nav.cartStyles, defaults.cartStyles);

  // Schema 15: the mobile shell/bar are copied once from the effective schema-14 surfaces,
  // then become independent owners. No live desktop fallback remains after this point.
  nav.mobile.shellStyle = mergeMissing(nav.mobile.shellStyle, nav.shellStyle);
  nav.mobile.barStyle = mergeMissing(nav.mobile.barStyle, nav.barStyle);
  nav.mobile = mergeMissing(nav.mobile, defaults.mobile);

  delete nav.mobile.socialStyle;

  // Shared schema-14 mobile presentation is migration-only input. Structural/asset fields remain.
  [
    "drawerBackgroundColor", "drawerTextColor", "drawerMutedTextColor", "drawerDividerColor", "itemHoverBackgroundColor",
    "fontSize", "fontWeight", "itemMinHeight",
    "hamburgerColor", "hamburgerBackgroundColor", "hamburgerHoverColor", "hamburgerHoverBackgroundColor", "hamburgerBorderColor", "hamburgerSize",
  ].forEach((key) => delete nav.mobile[key]);

  if (Array.isArray(source?.layout?.segments)) {
    source.layout.segments = source.layout.segments.map((segment) => {
      if (!(segment?.id === "navbar" || segment?.type === "navbar")) return segment;
      const next = clone(segment);
      if (next?.visualStyle && typeof next.visualStyle === "object") {
        delete next.visualStyle.elementColors;
        delete next.visualStyle.colors;
        delete next.visualStyle.useGlobalColors;
      }
      return next;
    });
  }

  return source;
}

export function navbarLinkOwner(navigation, key, device = "desktop") {
  const nav = objectValue(navigation);
  const defaults = createNavbarVisualOwnershipDefaults({}, { style: "style-1" }, {}).links[key] || {};
  const owner = objectValue(nav.links?.[key]);
  return mergeMissing(objectValue(device === "mobile" ? owner.mobileStyle : owner.desktopStyle), device === "mobile" ? defaults.mobileStyle : defaults.desktopStyle);
}

export function navbarCustomButtonOwner(button, device = "desktop") {
  const defaults = createNavbarVisualOwnershipDefaults({}, { style: "style-1" }, {});
  const fallback = device === "mobile" ? defaults.customButtonMobileStyle : defaults.customButtonDesktopStyle;
  return mergeMissing(objectValue(device === "mobile" ? button?.mobileStyle : button?.desktopStyle), fallback);
}

export function navbarActionOwner(navigation, owner, device = "desktop") {
  const nav = objectValue(navigation);
  const defaults = createNavbarVisualOwnershipDefaults({}, { style: "style-1" }, {});
  const source = owner === "login" ? nav.loginStyles : owner === "account" ? nav.accountStyles : nav.cartStyles;
  const fallbackSource = owner === "login" ? defaults.loginStyles : owner === "account" ? defaults.accountStyles : defaults.cartStyles;
  return mergeMissing(objectValue(source?.[device]), objectValue(fallbackSource?.[device]));
}

export function navbarLogoSurfaceOwner(navigation, device = "desktop") {
  const defaults = createNavbarVisualOwnershipDefaults({}, { style: "style-1" }, {}).logoSurfaceStyle;
  return mergeMissing(objectValue(navigation?.logoSurfaceStyle?.[device]), objectValue(defaults?.[device]));
}

export function navbarBusinessNameColor(navigation, device = "desktop") {
  const defaults = createNavbarVisualOwnershipDefaults({}, { style: "style-1" }, {}).businessNameStyle;
  return configured(navigation?.businessNameStyle?.[device]?.color, defaults?.[device]?.color || "#19242F");
}

export function navbarSurfaceOwner(navigation, key, device = "desktop") {
  const defaults = createNavbarVisualOwnershipDefaults({}, { style: "style-1" }, {});
  if (device === "mobile" && ["shellStyle", "barStyle"].includes(key)) {
    return mergeMissing(objectValue(navigation?.mobile?.[key]), objectValue(defaults.mobile?.[key]));
  }
  return mergeMissing(objectValue(navigation?.[key]), objectValue(defaults?.[key]));
}

export function navbarMobileOwner(navigation, key) {
  const defaults = createNavbarVisualOwnershipDefaults({}, { style: "style-1" }, {}).mobile;
  return mergeMissing(objectValue(navigation?.mobile?.[key]), objectValue(defaults?.[key]));
}

export { STANDARD_LINK_KEYS };
